import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthLayout } from "@/components/AuthLayout";
import { VerifyCodeStep } from "@/components/VerifyCodeStep";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { id: "name", label: "Full name", type: "text", required: true },
  { id: "email", label: "Email", type: "email", required: true },
  { id: "phone", label: "Mobile (optional, E.164 e.g. +447700900123)", type: "tel", required: false },
  { id: "password", label: "Password (min 8 characters)", type: "password", required: true },
];

export default function Register() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/register", { ...form, phone: form.phone || null, accept_terms: agreed });
      setPending(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const footer = <>Already have an account? <Link data-testid="goto-login-link" to={`/login?next=${encodeURIComponent(next)}`} className="underline font-medium text-slate-900 dark:text-white hover:text-emerald-600">Sign in</Link></>;

  if (pending) {
    return (
      <AuthLayout title="Confirm your email" subtitle="One last step to activate your mailbox." footer={footer}>
        <VerifyCodeStep email={form.email.toLowerCase()} endpoint="/auth/verify-email"
          onSuccess={(d) => { setUser(d.user); nav(next, { replace: true }); }}
          resendEndpoint="/auth/login" resendBody={{ email: form.email, password: form.password }} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your account" subtitle="Send and receive verified letters." footer={footer}>
      <form onSubmit={submit} className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.id} className="space-y-2">
            <Label htmlFor={f.id}>{f.label}</Label>
            <Input id={f.id} data-testid={`register-${f.id}-input`} type={f.type} required={f.required} value={form[f.id]}
              onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} className="h-11" />
          </div>
        ))}
        <div className="flex items-start gap-3 pt-1">
          <Checkbox id="accept-terms" data-testid="register-terms-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
          <Label htmlFor="accept-terms" className="text-sm font-normal leading-relaxed">
            I agree to the <Link to="/terms" target="_blank" data-testid="register-terms-link" className="underline hover:text-emerald-600">Terms of Service</Link> and{" "}
            <Link to="/privacy" target="_blank" data-testid="register-privacy-link" className="underline hover:text-emerald-600">Privacy Policy</Link>
          </Label>
        </div>
        {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
        <Button data-testid="register-submit-btn" type="submit" disabled={busy || !agreed} className="w-full h-11 rounded-full">{busy && <Loader2 className="animate-spin mr-2" size={16} />}Create account</Button>
      </form>
    </AuthLayout>
  );
}
